import React from "react";

/**
 * SlideSwitch v3:
 * - Current view is a normal (non-absolute) block so it's always visible.
 * - Next view overlays and slides in from the right.
 * - Vertical scrolling works inside each panel.
 * - Gracefully handles missing view keys.
 */
export function SlideSwitch({
  activeKey,
  views,
  durationMs = 300,
  maxHeightClass = "max-h-[78vh]",
  minHeightClass = "min-h-[320px]",
}: {
  activeKey: string;
  views: Record<string, React.ReactNode>;
  durationMs?: number;
  maxHeightClass?: string; // bounds panel height so inner scroll works
  minHeightClass?: string; // prevents collapse on short content
}) {
  const [currentKey, setCurrentKey] = React.useState(activeKey);
  const [nextKey, setNextKey] = React.useState<string | null>(null);
  const [phase, setPhase] = React.useState<"idle" | "staging" | "animating">("idle");

  const currentView = views[currentKey];
  const incomingView = nextKey ? views[nextKey] : null;

  // Keep refs to scrollable containers per key
  const panelRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  // Guard: if missing current view, fall back to first available
  React.useEffect(() => {
    if (!views[currentKey]) {
      const first = Object.keys(views)[0];
      if (first) setCurrentKey(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [views]);

  // Kick off transition when activeKey changes
  React.useEffect(() => {
    if (activeKey === currentKey || phase !== "idle") return;
    if (!views[activeKey]) {
      // no view for the requested key; do nothing
      return;
    }
    setNextKey(activeKey);
    setPhase("staging"); // mount incoming offscreen
    const id = requestAnimationFrame(() => setPhase("animating"));
    return () => cancelAnimationFrame(id);
  }, [activeKey, currentKey, phase, views]);

  // Complete transition
  React.useEffect(() => {
    if (phase !== "animating") return;
    const t = setTimeout(() => {
      if (nextKey) setCurrentKey(nextKey);
      setNextKey(null);
      setPhase("idle");
    }, durationMs);
    return () => clearTimeout(t);
  }, [phase, nextKey, durationMs]);

  // Force scrollTop = 0 throughout the entire transition process - with debugging
  React.useEffect(() => {
    const resetScroll = () => {
      console.log("SlideSwitch: attempting scroll reset for domain", currentKey);
      
      // Target the main scrollable container
      const mainScrollContainer = document.querySelector('.h-\\[calc\\(100vh-6rem\\)\\]');
      if (mainScrollContainer) {
        console.log("SlideSwitch: main container before reset:", mainScrollContainer.scrollTop);
        mainScrollContainer.scrollTop = 0;
        console.log("SlideSwitch: main container after reset:", mainScrollContainer.scrollTop);
      } else {
        console.log("SlideSwitch: main container not found");
      }
      
      // Try to find any scrollable parent containers
      const allScrollable = document.querySelectorAll('*');
      Array.from(allScrollable).forEach(el => {
        if (el instanceof HTMLElement) {
          const styles = window.getComputedStyle(el);
          if ((styles.overflowY === 'auto' || styles.overflowY === 'scroll') && el.scrollTop > 0) {
            console.log("SlideSwitch: found scrollable element with scrollTop:", el.scrollTop, el.className);
            el.scrollTop = 0;
          }
        }
      });
      
      // Also reset document and window scroll
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo(0, 0);
    };

    // Reset when transition starts
    if (phase === "staging") {
      console.log("SlideSwitch: staging phase, resetting scroll");
      resetScroll();
    }
    
    // Reset when transition ends
    if (phase === "idle") {
      console.log("SlideSwitch: idle phase, resetting scroll");
      resetScroll();
      // Multiple resets to ensure it sticks
      setTimeout(() => {
        console.log("SlideSwitch: delayed reset 1");
        resetScroll();
      }, 10);
      setTimeout(() => {
        console.log("SlideSwitch: delayed reset 2");  
        resetScroll();
      }, 100);
    }
  }, [phase, currentKey]);

  // Force reset on every domain change
  React.useEffect(() => {
    console.log("SlideSwitch: domain changed to", currentKey);
    
    // Use requestAnimationFrame to ensure this runs after React updates
    requestAnimationFrame(() => {
      const mainScrollContainer = document.querySelector('.h-\\[calc\\(100vh-6rem\\)\\]');
      if (mainScrollContainer) {
        console.log("SlideSwitch: forcing scroll reset on domain change, before:", mainScrollContainer.scrollTop);
        mainScrollContainer.scrollTop = 0;
        console.log("SlideSwitch: after:", mainScrollContainer.scrollTop);
      }
    });
  }, [currentKey]);

  return (
    <div className={`relative w-full overflow-x-hidden ${maxHeightClass} ${minHeightClass}`}>
      {/* CURRENT: normal flow block so it always has height; remove overflow-y-auto to prevent nested scrolling */}
      <div
        ref={(el) => (panelRefs.current[currentKey] = el)}
        className={[
          "relative transition-all ease-out duration-300",
          phase === "animating" ? "-translate-x-10 opacity-0" : "translate-x-0 opacity-100",
        ].join(" ")}
        style={{ pointerEvents: phase === "animating" ? "none" : "auto" }}
        key={`curr-${currentKey}`}
      >
        {currentView ?? (
          <div className="p-4 text-sm text-slate-500">No view for key: {currentKey}</div>
        )}
      </div>

      {/* NEXT: overlays and slides in from right only during transition; remove overflow-y-auto */}
      {nextKey && incomingView && (
        <div
          ref={(el) => (panelRefs.current[nextKey] = el)}
          className={[
            "absolute inset-0 transition-all ease-out duration-300",
            phase === "staging" ? "translate-x-full opacity-0" : "translate-x-0 opacity-100",
          ].join(" ")}
          style={{ pointerEvents: "none" }}
          key={`next-${nextKey}`}
        >
          {incomingView}
        </div>
      )}
    </div>
  );
}